import { useEffect, useState } from 'react';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import Button from '../../../components/Button';
import { UserLinks } from '../../../components/UserLinks';
import { userService } from '../../../services/userService';
import type { ObsessLessUser } from '../../../types/commonTypes';

const PAGE_SIZE = 20;

export const JourneyLeaderboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [users, setUsers] = useState<ObsessLessUser[]>([]);
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchTopUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await userService.getTopJourneyUsers(PAGE_SIZE);
            setUsers(result.users);
            setLastDoc(result.lastDoc);
            setHasMore(result.lastDoc !== null);
        } catch (err) {
            console.error('Error fetching top journey users:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch users');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMore = async () => {
        if (!lastDoc || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const result = await userService.getTopJourneyUsers(PAGE_SIZE, lastDoc);
            setUsers((prev) => [...prev, ...result.users]);
            setLastDoc(result.lastDoc);
            setHasMore(result.lastDoc !== null);
        } catch (err) {
            console.error('Error loading more journey users:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchTopUsers();
    }, []);

    const formatDate = (date: Date | undefined): string => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading top journey users...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={fetchTopUsers} variant="primary">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Journey Leaderboard</h2>
                <Button onClick={fetchTopUsers} variant="primary">
                    Refresh
                </Button>
            </div>

            {users.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    No users with quest journey data found.
                </div>
            ) : (
                <>
                    <div className="mb-4 text-sm text-gray-600">
                        Showing top {users.length} users by journey progress
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rank
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Current Day
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Day Completed
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Journey Started
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((user, index) => (
                                    <tr
                                        key={user.uid}
                                        className={index < 3 ? 'bg-yellow-50' : ''}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <span className={`text-sm font-bold ${index === 0 ? 'text-yellow-600 text-lg' :
                                                    index === 1 ? 'text-gray-500 text-lg' :
                                                        index === 2 ? 'text-orange-600 text-lg' :
                                                            'text-gray-900'
                                                    }`}>
                                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {user.email || 'No email'}
                                            </div>
                                            {user.displayName && (
                                                <div className="text-sm text-gray-500">
                                                    {user.displayName}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-blue-600">
                                                Day {user.questJourney?.currentDay}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.questJourney?.isCurrentDayCompleted
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {user.questJourney?.isCurrentDayCompleted ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(user.questJourney?.journeyDayStartedAt)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <UserLinks userId={user.uid} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <div className="flex justify-center mt-6">
                            <Button onClick={handleLoadMore} variant="primary" disabled={isLoadingMore}>
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
